import React from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { UserPlus } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';

export const NuevoPropietarioDialog = ({ handleCreateNuevoPropietario, isNuevoPropietarioOpen, nuevoPropietarioForm, nuevoPropietarioTab, saving, setIsNuevoPropietarioOpen, setNuevoPropietarioForm, setNuevoPropietarioTab }) => {
  const form = nuevoPropietarioForm;
  const set = (patch) => setNuevoPropietarioForm({ ...form, ...patch });
  const esHumana = form.tipo === 'persona_humana' || form.tipo === 'persona_fisica';
  const contactoMismo = form.contact_is_same_as_owner !== false; // default sí

  return (
    <Dialog open={isNuevoPropietarioOpen} onOpenChange={(open) => { setIsNuevoPropietarioOpen(open); if (!open) setNuevoPropietarioTab('titular'); }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-green-600" />
            Nuevo Propietario
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleCreateNuevoPropietario}>
          <Tabs value={nuevoPropietarioTab} onValueChange={setNuevoPropietarioTab} className="w-full">
            <TabsList className="grid w-full grid-cols-4 mb-4">
              <TabsTrigger value="titular" data-testid="tab-titular">Titular</TabsTrigger>
              <TabsTrigger value="contacto" data-testid="tab-contacto">Contacto</TabsTrigger>
              <TabsTrigger value="fiscal" data-testid="tab-fiscal">Fiscal</TabsTrigger>
              <TabsTrigger value="bancario" data-testid="tab-bancario">Bancario</TabsTrigger>
            </TabsList>

            {/* ─── Titular ─── */}
            <TabsContent value="titular" className="space-y-4">
              <div className="space-y-2">
                <Label>Tipo de Persona *</Label>
                <Select value={form.tipo} onValueChange={(v) => set({ tipo: v })}>
                  <SelectTrigger data-testid="select-tipo-persona"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="persona_humana">Persona Humana</SelectItem>
                    <SelectItem value="persona_juridica">Persona Jurídica</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {esHumana ? (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nombre *</Label>
                    <Input
                      value={form.nombre || ''}
                      onChange={(e) => set({ nombre: e.target.value })}
                      placeholder="Juan"
                      required
                      data-testid="titular-nombre"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Apellido *</Label>
                    <Input
                      value={form.apellido || ''}
                      onChange={(e) => set({ apellido: e.target.value })}
                      placeholder="Pérez"
                      required
                      data-testid="titular-apellido"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Razón Social *</Label>
                  <Input
                    value={form.razon_social || ''}
                    onChange={(e) => set({ razon_social: e.target.value })}
                    placeholder="Empresa S.A."
                    required
                    data-testid="titular-razon-social"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label>DNI / CUIT / CUIL</Label>
                <Input
                  value={form.dni || ''}
                  onChange={(e) => set({ dni: e.target.value })}
                  placeholder={esHumana ? '12.345.678' : '30-12345678-9'}
                  data-testid="titular-dni"
                />
              </div>

              <div className="space-y-2">
                <Label>Dirección legal</Label>
                <Input
                  value={form.direccion || ''}
                  onChange={(e) => set({ direccion: e.target.value })}
                  placeholder="Domicilio del titular"
                  data-testid="titular-direccion"
                />
              </div>

              <div className="space-y-2">
                <Label>Email legal</Label>
                <Input
                  type="email"
                  value={form.email_legal || ''}
                  onChange={(e) => set({ email_legal: e.target.value })}
                  placeholder="legal@ejemplo.com (opcional)"
                  data-testid="titular-email-legal"
                />
              </div>

              <div className="space-y-2">
                <Label>Notas</Label>
                <Input
                  value={form.notas || ''}
                  onChange={(e) => set({ notas: e.target.value })}
                  placeholder="Notas adicionales"
                />
              </div>
            </TabsContent>

            {/* ─── Contacto ─── */}
            <TabsContent value="contacto" className="space-y-4">
              <div className="space-y-2">
                <Label>¿La persona de contacto es la misma que el titular?</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={contactoMismo ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => set({ contact_is_same_as_owner: true })}
                    data-testid="contacto-mismo-si"
                  >Sí</Button>
                  <Button
                    type="button"
                    variant={!contactoMismo ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => set({ contact_is_same_as_owner: false })}
                    data-testid="contacto-mismo-no"
                  >No</Button>
                </div>
              </div>

              {!contactoMismo && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nombre de contacto *</Label>
                    <Input
                      value={form.contact_person_first_name || ''}
                      onChange={(e) => set({ contact_person_first_name: e.target.value })}
                      placeholder="Juan"
                      required={!contactoMismo}
                      data-testid="contacto-nombre"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Apellido de contacto *</Label>
                    <Input
                      value={form.contact_person_last_name || ''}
                      onChange={(e) => set({ contact_person_last_name: e.target.value })}
                      placeholder="Pérez"
                      required={!contactoMismo}
                      data-testid="contacto-apellido"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>Usuario / email de acceso *</Label>
                <Input
                  type="email"
                  value={form.email || ''}
                  onChange={(e) => set({ email: e.target.value })}
                  placeholder="usuario@ejemplo.com"
                  required
                  data-testid="contacto-email-acceso"
                />
                <p className="text-xs text-slate-500">Este email se usa también para que el propietario acceda al portal.</p>
              </div>

              <div className="space-y-2">
                <Label>WhatsApp</Label>
                <Input
                  value={form.whatsapp || ''}
                  onChange={(e) => set({ whatsapp: e.target.value })}
                  placeholder="+54 9 261 123 4567"
                  data-testid="contacto-whatsapp"
                />
              </div>

              <div className="space-y-2">
                <Label>Preferencia de contacto</Label>
                <Select value={form.preferencia_contacto} onValueChange={(v) => set({ preferencia_contacto: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </TabsContent>

            {/* ─── Fiscal ─── */}
            <TabsContent value="fiscal" className="space-y-4">
              <div className="space-y-2">
                <Label>Nombre / Razón Social fiscal</Label>
                <Input
                  value={form.razon_social_fiscal || ''}
                  onChange={(e) => set({ razon_social_fiscal: e.target.value })}
                  placeholder="Nombre que figura en facturación (puede diferir del titular)"
                  data-testid="fiscal-razon-social"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>CUIT/CUIL</Label>
                  <Input
                    value={form.cuit_cuil || ''}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, '');
                      if (value.length > 11) value = value.slice(0, 11);
                      let formatted = '';
                      if (value.length > 0) formatted = value.slice(0, 2);
                      if (value.length > 2) formatted += '-' + value.slice(2, 10);
                      if (value.length > 10) formatted += '-' + value.slice(10, 11);
                      set({ cuit_cuil: formatted });
                    }}
                    placeholder="20-12345678-9"
                    maxLength={13}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Condición IVA</Label>
                  <Select value={form.condicion_iva} onValueChange={(v) => set({ condicion_iva: v })}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="responsable_inscripto">Responsable Inscripto</SelectItem>
                      <SelectItem value="monotributista">Monotributista</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Domicilio Fiscal</Label>
                <Input
                  value={form.domicilio_fiscal || ''}
                  onChange={(e) => set({ domicilio_fiscal: e.target.value })}
                  placeholder="Domicilio según AFIP"
                />
              </div>

              <div className="space-y-2">
                <Label>Email de facturación</Label>
                <Input
                  type="email"
                  value={form.email_facturacion || ''}
                  onChange={(e) => set({ email_facturacion: e.target.value })}
                  placeholder="facturas@ejemplo.com"
                />
              </div>
            </TabsContent>

            {/* ─── Bancario ─── */}
            <TabsContent value="bancario" className="space-y-4">
              <div className="space-y-2">
                <Label>CBU</Label>
                <Input
                  value={form.cbu || ''}
                  onChange={(e) => set({ cbu: e.target.value })}
                  placeholder="0000000000000000000000"
                  maxLength={22}
                />
              </div>
              <div className="space-y-2">
                <Label>Alias</Label>
                <Input
                  value={form.alias_cbu || ''}
                  onChange={(e) => set({ alias_cbu: e.target.value })}
                  placeholder="mi.alias"
                />
              </div>
              <div className="space-y-2">
                <Label>Banco</Label>
                <Input
                  value={form.banco || ''}
                  onChange={(e) => set({ banco: e.target.value })}
                  placeholder="Banco Nación, Santander, etc."
                />
              </div>
              <div className="space-y-2">
                <Label>Titular de la Cuenta</Label>
                <Input
                  value={form.titular_cuenta || ''}
                  onChange={(e) => set({ titular_cuenta: e.target.value })}
                  placeholder="Nombre del titular"
                />
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={() => setIsNuevoPropietarioOpen(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving} data-testid="submit-nuevo-propietario">{saving ? 'Creando...' : 'Crear Propietario'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
